import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Redirect, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiGuideUrl from './apiGuide.txt';
import webhookGuideUrl from './webhookGuide.txt';
import marketingOptinGuideUrl from './marketingOptinGuide.txt';

// The guides are markdown in .txt files (.txt because the IIS host 404s on .md).
// This renders the small subset it uses: #/##/### headings, paragraphs,
// "- " and "1. " lists, pipe tables, ``` code blocks, ![alt](/path) images on their own line,
// and inline `code` / **bold** / [link](#anchor).
// Lines between <!-- webhook --> and <!-- /webhook --> are only shown to users with webhooks enabled.

const canSeeWebhooks = () => Boolean(JSON.parse(localStorage.getItem('user'))?.features?.enableWebhook);

function filterWebhookLines(markdown, showWebhooks) {
  let hidden = false;
  return markdown
    .split('\n')
    .filter((line) => {
      if (line.trim() === '<!-- webhook -->') {
        hidden = !showWebhooks;
        return false;
      }
      if (line.trim() === '<!-- /webhook -->') {
        hidden = false;
        return false;
      }
      return !hidden;
    })
    .join('\n');
}

const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const codeSx = {
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize: '0.85em',
  bgcolor: 'action.hover',
  px: 0.5,
  borderRadius: 0.5,
};

function renderInline(text) {
  // Split on **bold**, `code` and [link](href); bold may wrap code (**`GET /x`**)
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g).filter(Boolean);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      // App paths (/docs/webhooks, /docs#anchor) go through the router; #anchor stays in-page
      const routerProps = link[2].startsWith('/') ? { component: RouterLink, to: link[2] } : { href: link[2] };
      return (
        <Link key={i} {...routerProps} underline="hover">
          {renderInline(link[1])}
        </Link>
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{renderInline(part.slice(2, -2))}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <Box component="code" key={i} sx={codeSx}>
          {part.slice(1, -1)}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function parseBlocks(markdown) {
  const lines = markdown.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const body = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) body.push(lines[i++]);
      i++;
      blocks.push({ type: 'code', text: body.join('\n') });
    } else if (/^#{1,3} /.test(line)) {
      const level = line.indexOf(' ');
      blocks.push({ type: 'heading', level, text: line.slice(level + 1) });
      i++;
    } else if (line.startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        const cells = lines[i]
          .slice(1, -1)
          .split('|')
          .map((c) => c.trim());
        if (!cells.every((c) => /^-+$/.test(c))) rows.push(cells);
        i++;
      }
      blocks.push({ type: 'table', head: rows[0], rows: rows.slice(1) });
    } else if (line.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].startsWith('- ')) items.push(lines[i++].slice(2));
      blocks.push({ type: 'list', items });
    } else if (/^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) items.push(lines[i++].replace(/^\d+\. /, ''));
      blocks.push({ type: 'list', ordered: true, items });
    } else if (/^!\[[^\]]*\]\([^)]+\)$/.test(line.trim())) {
      const [, alt, src] = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      blocks.push({ type: 'image', alt, src });
      i++;
    } else if (line.trim()) {
      blocks.push({ type: 'paragraph', text: line });
      i++;
    } else {
      i++;
    }
  }
  return blocks;
}

function Block({ block }) {
  switch (block.type) {
    case 'heading': {
      const variant = { 1: 'h4', 2: 'h5', 3: 'h6' }[block.level];
      return (
        <Typography
          id={slug(block.text)}
          variant={variant}
          component={`h${block.level}`}
          sx={{ mt: block.level === 1 ? 0 : block.level === 2 ? 5 : 3, mb: 1.5, fontWeight: 600, scrollMarginTop: 16 }}
        >
          {block.text}
        </Typography>
      );
    }
    case 'code':
      return (
        <Box
          component="pre"
          sx={{
            ...codeSx,
            fontSize: '0.8rem',
            p: 2,
            my: 1.5,
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {block.text}
        </Box>
      );
    case 'table':
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ my: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {block.head.map((cell, i) => (
                  <TableCell key={i} sx={{ fontWeight: 600 }}>
                    {renderInline(cell)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {block.rows.map((row, r) => (
                <TableRow key={r}>
                  {row.map((cell, c) => (
                    <TableCell key={c}>{renderInline(cell)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    case 'image':
      return (
        <Box
          component="img"
          src={block.src}
          alt={block.alt}
          sx={{ display: 'block', maxWidth: '100%', my: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
        />
      );
    case 'list':
      return (
        <Box component={block.ordered ? 'ol' : 'ul'} sx={{ my: 1, pl: 3 }}>
          {block.items.map((item, i) => (
            <Typography component="li" key={i} sx={{ mb: 0.5 }}>
              {renderInline(item)}
            </Typography>
          ))}
        </Box>
      );
    default:
      return <Typography sx={{ my: 1 }}>{renderInline(block.text)}</Typography>;
  }
}

const GuidePage = ({ guideUrl, backTo, backLabel }) => {
  const [blocks, setBlocks] = useState(null);
  const [error, setError] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    setBlocks(null);
    setError(false);
    fetch(guideUrl)
      .then((res) => res.text())
      .then((text) => setBlocks(parseBlocks(filterWebhookLines(text, canSeeWebhooks()))))
      .catch(() => setError(true));
  }, [guideUrl]);

  // The browser can't jump to #anchor before the guide is fetched, so do it once it renders
  useEffect(() => {
    if (blocks && hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [blocks, hash]);

  const sections = (blocks || []).filter((b) => b.type === 'heading' && b.level === 2);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Button component={RouterLink} to={backTo} startIcon={<ArrowBackIcon />} sx={{ mb: 3 }}>
          {backLabel}
        </Button>

        {error && <Typography color="error">Could not load the API guide. Please refresh the page.</Typography>}
        {!blocks && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {blocks && (
          <>
            <Block block={blocks[0]} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {sections.map((s) => (
                <Link key={s.text} href={`#${slug(s.text)}`} underline="hover">
                  {renderInline(s.text)}
                </Link>
              ))}
            </Box>
            {blocks.slice(1).map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </>
        )}
      </Container>
    </Box>
  );
};

const ApiDocs = () => <GuidePage guideUrl={apiGuideUrl} backTo="/dashboard" backLabel="Back to dashboard" />;

export const WebhookDocs = () =>
  canSeeWebhooks() ? (
    <GuidePage guideUrl={webhookGuideUrl} backTo="/docs" backLabel="Back to API guide" />
  ) : (
    <Redirect to="/docs" />
  );

// Not linked from anywhere; open /docs/guides/marketing-optin directly
export const MarketingOptinGuide = () => (
  <GuidePage guideUrl={marketingOptinGuideUrl} backTo="/dashboard" backLabel="Back to dashboard" />
);

export default ApiDocs;
